import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Bot, Send, RotateCcw, Loader2, Calendar as CalendarIcon } from "lucide-react";
import InlineBookingWidget from "@/components/InlineBookingWidget";

/* ---------------------------------------------------------------------------
 *  KaiChatPanel — the live chat UI used inside the Home page KAI section
 *  and re-usable anywhere else on the site.
 *
 *  🔌  N8N WEBHOOK CONFIGURATION
 *  --------------------------------------------------------------------------
 *  Replace `N8N_WEBHOOK_URL` below with the production webhook URL from n8n.
 *  The webhook is expected to accept a JSON POST body and return a JSON
 *  response with a `reply` field. If you need to tweak the payload shape,
 *  edit `buildPayload()` and `parseReply()` below — they are the only two
 *  places that touch the wire format.
 * ------------------------------------------------------------------------- */
export const N8N_WEBHOOK_URL =
  "https://kyvixe.app.n8n.cloud/webhook/ca9edb1a-6ce7-4347-9220-9639a41b0b1f";

// build the JSON body sent to n8n — tweak fields as you like
const buildPayload = ({ message, sessionId, history }) => ({
  message,
  sessionId,
  history: history.map((m) => ({ role: m.role, content: m.content })),
});

// extract the assistant reply from any of the common n8n response shapes
const parseReply = (data) => {
  if (data == null) return "";
  if (typeof data === "string") return data;
  return (
    data.reply ??
    data.output ??
    data.message ??
    data.text ??
    data.response ??
    JSON.stringify(data)
  );
};

const genId = () =>
  typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`;

const timeLabel = (ts) =>
  new Date(ts).toLocaleTimeString("en", { hour: "2-digit", minute: "2-digit" });

const INITIAL_GREETING = {
  id: "greeting",
  role: "assistant",
  content:
    "hi 👋 i'm kai — kyvixe's ai consultant. tell me about your business and i'll show you what we can automate first.",
  ts: Date.now(),
};

const SUGGESTIONS = [
  "what does kyvixe do?",
  "how much does a chatbot cost?",
  "book a discovery call",
  "how fast can you ship a website?",
];

export default function KaiChatPanel({ height = "h-[560px]" }) {
  const [messages, setMessages] = React.useState([INITIAL_GREETING]);
  const [input, setInput] = React.useState("");
  const [sending, setSending] = React.useState(false);
  const [error, setError] = React.useState("");
  const sessionIdRef = React.useRef(genId());
  const scrollRef = React.useRef(null);
  const inputRef = React.useRef(null);

  React.useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
  }, [messages, sending]);

  // trigger for automatic inline booking widget when kai suggests it
  const shouldShowBookingWidget = (text) => {
    if (!text) return false;
    const t = text.toLowerCase();
    return (
      t.includes("[book]") ||
      /book(?:\s+(?:a|your))?\s+(?:call|discovery|meeting|appointment)/.test(t)
    );
  };

  const cleanReply = (text) => (text || "").replace(/\[book\]/gi, "").trim();

  const openBookingWidget = () => {
    setMessages((m) => [
      ...m,
      {
        id: genId(),
        role: "assistant",
        type: "booking",
        content: "pick a time that works for you and i'll lock it in.",
        ts: Date.now(),
      },
    ]);
  };

  const send = async (raw) => {
    const text = (raw ?? input).trim();
    if (!text || sending) return;

    const userMsg = { id: genId(), role: "user", content: text, ts: Date.now() };
    const history = [...messages, userMsg];

    setMessages(history);
    setInput("");
    setSending(true);
    setError("");

    try {
      const res = await fetch(N8N_WEBHOOK_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(
          buildPayload({
            message: text,
            sessionId: sessionIdRef.current,
            history,
          }),
        ),
      });
      if (!res.ok) throw new Error(`webhook returned ${res.status}`);
      const ct = res.headers.get("content-type") || "";
      const data = ct.includes("application/json") ? await res.json() : await res.text();
      const rawReply = parseReply(data) || "…";
      const showBook = shouldShowBookingWidget(rawReply) || shouldShowBookingWidget(text);
      const cleaned = cleanReply(rawReply) || (showBook ? "sure — let's find a time." : "…");
      setMessages((m) => [
        ...m,
        { id: genId(), role: "assistant", content: cleaned, ts: Date.now() },
        ...(showBook
          ? [{
              id: genId(),
              role: "assistant",
              type: "booking",
              content: "pick a time that works for you and i'll lock it in.",
              ts: Date.now(),
            }]
          : []),
      ]);
    } catch (e) {
      setError(e.message || "something went wrong. please try again.");
      setMessages((m) => [
        ...m,
        {
          id: genId(),
          role: "assistant",
          content:
            "sorry — i couldn't reach the ai just now. please try again in a moment or use the contact form.",
          ts: Date.now(),
          errored: true,
        },
      ]);
    } finally {
      setSending(false);
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  };

  const reset = () => {
    sessionIdRef.current = genId();
    setMessages([INITIAL_GREETING]);
    setError("");
  };

  return (
    <div
      className={`kyv-card overflow-hidden flex flex-col ${height}`}
      data-testid="kai-chat-panel"
    >
      {/* header */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-white/10 bg-white/[0.02]">
        <div className="flex items-center gap-3">
          <div className="relative h-10 w-10 rounded-full bg-blue-500/15 border border-blue-400/30 flex items-center justify-center text-blue-400">
            <Bot size={18} />
            <span className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full bg-emerald-400 border-2 border-[#12121a] animate-pulse" />
          </div>
          <div>
            <p className="font-display lowercase font-bold text-lg leading-none">kai</p>
            <p className="text-xs text-blue-400 mt-1">ai consultant · online</p>
          </div>
        </div>
        <button
          onClick={reset}
          className="text-xs text-gray-400 hover:text-blue-300 inline-flex items-center gap-1.5"
          data-testid="kai-reset-btn"
          type="button"
        >
          <RotateCcw size={12} /> new chat
        </button>
      </div>

      {/* persistent "book a call" pill — always available */}
      <div className="px-5 pt-3">
        <button
          type="button"
          onClick={openBookingWidget}
          className="inline-flex items-center gap-2 text-xs px-3 py-1.5 rounded-full bg-blue-500/10 border border-blue-500/40 text-blue-300 hover:bg-blue-500/20 hover:border-blue-400/60 transition"
          data-testid="kai-book-a-call-pill"
        >
          <CalendarIcon size={12} /> book a call inside chat
        </button>
      </div>

      {/* messages */}
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto px-5 py-5 space-y-4"
        data-testid="kai-messages"
      >
        <AnimatePresence initial={false}>
          {messages.map((m) => {
            if (m.type === "booking") {
              return (
                <motion.div
                  key={m.id}
                  layout
                  initial={{ opacity: 0, y: 10, scale: 0.98 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  transition={{ duration: 0.3 }}
                  className="flex justify-start"
                >
                  <div className="max-w-[95%] w-full">
                    <p className="text-xs text-blue-100/80 bg-blue-600 inline-block px-3 py-1.5 rounded-2xl rounded-bl-sm mb-2">
                      {m.content}
                    </p>
                    <InlineBookingWidget
                      onBooked={(b) =>
                        setMessages((mm) => [
                          ...mm,
                          {
                            id: genId(),
                            role: "assistant",
                            content: `booked ✓ ${b.date} at ${b.time}. i've saved it and a confirmation is on the way to ${b.email}.`,
                            ts: Date.now(),
                          },
                        ])
                      }
                    />
                  </div>
                </motion.div>
              );
            }
            return (
              <motion.div
                key={m.id}
                layout
                initial={{ opacity: 0, y: 10, scale: 0.98 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 0.25 }}
                className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`max-w-[85%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap break-words ${
                    m.role === "user"
                      ? "bg-white/10 text-gray-100 rounded-br-sm"
                      : m.errored
                      ? "bg-red-500/15 text-red-200 border border-red-500/30 rounded-bl-sm"
                      : "bg-blue-600 text-white rounded-bl-sm"
                  }`}
                  data-testid={`kai-msg-${m.role}`}
                >
                  {m.content}
                  <div
                    className={`mt-1 text-[10px] tracking-wide ${
                      m.role === "user" ? "text-gray-400" : "text-blue-100/70"
                    }`}
                  >
                    {timeLabel(m.ts)}
                  </div>
                </div>
              </motion.div>
            );
          })}

          {sending && (
            <motion.div
              key="typing"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className="flex justify-start"
              data-testid="kai-typing"
            >
              <div className="px-4 py-3 rounded-2xl rounded-bl-sm bg-blue-600/70 flex items-center gap-1">
                <span
                  className="h-1.5 w-1.5 rounded-full bg-white/90 animate-bounce"
                  style={{ animationDelay: "0ms" }}
                />
                <span
                  className="h-1.5 w-1.5 rounded-full bg-white/90 animate-bounce"
                  style={{ animationDelay: "120ms" }}
                />
                <span
                  className="h-1.5 w-1.5 rounded-full bg-white/90 animate-bounce"
                  style={{ animationDelay: "240ms" }}
                />
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* quick suggestions — only on the initial greeting */}
      {messages.length === 1 && !sending && (
        <div className="px-5 pb-3 flex flex-wrap gap-2" data-testid="kai-suggestions">
          {SUGGESTIONS.map((s, i) => (
            <button
              key={i}
              type="button"
              onClick={() =>
                shouldShowBookingWidget(s) ? openBookingWidget() : send(s)
              }
              className="text-xs px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-gray-200 hover:border-blue-500/50 hover:text-blue-300 transition"
              data-testid={`kai-suggestion-${i}`}
            >
              {s}
            </button>
          ))}
        </div>
      )}

      {/* composer */}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          send();
        }}
        className="border-t border-white/10 bg-white/[0.02] px-3 py-3 sm:px-4 sm:py-4 flex items-end gap-2"
        data-testid="kai-composer"
      >
        <textarea
          ref={inputRef}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              send();
            }
          }}
          rows={1}
          placeholder="ask kai anything…"
          className="flex-1 resize-none bg-white/5 border border-white/10 rounded-2xl px-4 py-3 text-sm text-white placeholder:text-gray-500 focus:outline-none focus:border-blue-500/60 max-h-40"
          data-testid="kai-input"
          disabled={sending}
        />
        <button
          type="submit"
          disabled={sending || !input.trim()}
          className="kyv-btn-primary h-11 w-11 !p-0 flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed"
          aria-label="send"
          data-testid="kai-send-btn"
        >
          {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
        </button>
      </form>

      {error && (
        <p className="px-4 pb-3 text-xs text-red-400" data-testid="kai-error">
          {error}
        </p>
      )}
    </div>
  );
}
